import React, { useEffect, useCallback, FC } from 'react';

import { TextField } from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

import { ContactFormItem } from '../../types';
import { useAlert } from '../../utils/hooks';
import useValidation from '../../utils/useValidation';
import { CustomButton } from '../UI/CustomButton';
import { useContactFormStyles } from './styles';
import { contactFormSchema } from './validation';

const triggerSubmitEvent = () => {
  if (!document) return;

  let event;
  if (document.createEvent) {
    event = document.createEvent('HTMLEvents');
    event.initEvent('submit', true, true);
    event.eventName = 'submit';
    document.dispatchEvent(event);
  } else {
    // @ts-ignore
    event = document.createEventObject();
    event.eventName = 'submit';
    event.eventType = 'submit';
    // @ts-ignore
    document.fireEvent('on' + event.eventType, event);
  }
};

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const initialValues = {
  name: '',
  email: '',
  reason: null,
  message: '',
};

interface ContactFormProps {
  reasonItems: ContactFormItem[];
  userName?: string;
  userEmail?: string | null;
  userComFormId?: string | null;
  nameField?: string | null;
  emailField?: string | null;
  contactReasonField?: string | null;
  contactMessageField?: string | null;
}

export const ContactForm: FC<ContactFormProps> = ({
  reasonItems,
  userName,
  userEmail,
  userComFormId,
  nameField,
  emailField,
  contactReasonField,
  contactMessageField,
}) => {
  const classes = useContactFormStyles();
  const { t } = useTranslation();

  const { open, showAlert, hideAlert } = useAlert();

  const onSubmit = useCallback(
    async (values, { setDataValue }) => {
      if (window.userengage) {
        try {
          const event = values?.reason?.is_sales_request
            ? 'event.form_conversion'
            : 'event.contact_form_submitted';

          const name = values.name.split(' ');
          const firstName = name[0];
          const lastName = name.length > 1 ? name.slice(1).join(' ') : '';

          //used window.userengage instead of UE.pageHit, because it returns promise
          await window.userengage('client.update', {
            //name attribute does not update user name in user.com,just save it as custom attribute
            first_name: firstName,
            last_name: lastName,
            ...(emailField && { [emailField]: values.email }),
            ...(contactReasonField && { [contactReasonField]: values.reason?.type }),
            ...(contactMessageField && {
              [contactMessageField]: {
                message: values.message.trim(),
              },
            }),
          });

          await window.userengage(event, {
            form_id: userComFormId,
            ...(nameField && { [nameField]: values.name }),
            ...(emailField && { [emailField]: values.email }),
            ...(contactReasonField && { [contactReasonField]: values.reason?.type }),
            ...(contactMessageField && {
              [contactMessageField]: {
                message: values.message.trim(),
              },
            }),
          });

          showAlert();
          triggerSubmitEvent();

          setDataValue({
            ...initialValues,
            name: userName || '',
            email: userEmail || '',
          });
        } catch (e) {
          Sentry.captureException(e);
        }
      }
    },
    [
      showAlert,
      userName,
      userEmail,
      userComFormId,
      nameField,
      emailField,
      contactReasonField,
      contactMessageField,
    ],
  );

  const { values, errors, handleChange, setFieldManually, handleSubmit } = useValidation(
    initialValues,
    contactFormSchema,
    onSubmit,
  );

  useEffect(() => {
    if (userName) {
      setFieldManually('name', userName);
    }
  }, [userName, setFieldManually]);

  useEffect(() => {
    if (userEmail) {
      setFieldManually('email', userEmail);
    }
  }, [userEmail, setFieldManually]);

  return (
    <>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className={classes.contactForm}
      >
        <div className={classes.contactFormFieldWrap}>
          <TextField
            className={classes.contactFormField}
            name="name"
            label={t('input.placeholder.yourName')}
            variant="outlined"
            value={values.name}
            onChange={handleChange}
            error={Boolean(errors?.name)}
            helperText={errors?.name}
          />
        </div>
        <div className={classes.contactFormFieldWrap}>
          <TextField
            className={classes.contactFormField}
            name="email"
            label={t('input.placeholder.yourEmail')}
            variant="outlined"
            value={values.email}
            onChange={handleChange}
            error={Boolean(errors?.email)}
            helperText={errors?.email}
          />
        </div>
        <div className={classes.contactFormFieldWrap}>
          <Autocomplete
            options={reasonItems}
            getOptionLabel={(option) => option?.question || ''}
            value={values.reason}
            onChange={(event, value) => {
              setFieldManually('reason', value || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name="reason"
                label={t('input.placeholder.howCanWeHelp')}
                variant="outlined"
                error={Boolean(errors?.reason)}
                helperText={errors?.reason}
              />
            )}
          />
        </div>

        <TextField
          name="message"
          className={classes.contactFormField}
          label={t('input.placeholder.message')}
          variant="outlined"
          multiline
          rows={9}
          value={values.message}
          onChange={handleChange}
          error={Boolean(errors?.message)}
          helperText={errors?.message}
        />

        <CustomButton type="submit" btnClass={classes.contactFormButton}>
          {t('button.submit')}
        </CustomButton>
      </form>
      <Snackbar open={open} autoHideDuration={3000} onClose={hideAlert}>
        <Alert onClose={hideAlert} severity="success">
          {t('alert.messageSendSuccessfully')}
        </Alert>
      </Snackbar>
    </>
  );
};
